import React, { useState, useEffect, useRef } from 'react';
import { productsAPI, categoriesAPI } from '../../utils/api';
import { FaPlus, FaEdit, FaTrash, FaImage, FaUpload, FaFileImport } from 'react-icons/fa';
import '../../styles/AdminProducts.css';
import { useTranslation } from 'react-i18next';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState('products'); // 'products' or 'categories'
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [localStockValues, setLocalStockValues] = useState({});
  const stockUpdateTimers = useRef({});
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stockQuantity: '',
    unit: 'piece',
    multiplicity: 1,
    sellByBox: false,
    boxQuantity: 1,
    boxPrice: '',
    category: '',
    subCategory: '',
    isActive: true,
    discount: 0,
    generalDiscount: {
      discountType: 'none',
      discountValue: 0
    },
    nutrition: {
      calories: '',
      protein: '',
      fat: '',
      carbohydrates: '',
      fiber: '',
      servingSize: ''
    }
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadData();
    
    const timers = stockUpdateTimers.current;
    // Очистка всех таймеров при размонтировании компонента
    return () => {
      Object.values(timers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.getProducts({}), // Админ видит все товары без фильтров
        categoriesAPI.getCategories()
      ]);
      setProducts(productsRes.data.data);
      setCategories(categoriesRes.data.data);
      
      // Синхронизируем локальные значения с загруженными данными
      setLocalStockValues({});
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      
      // Правильно извлекаем generalDiscount из товара
      let generalDiscountData = { discountType: 'none', discountValue: 0 };
      
      if (product.generalDiscount && typeof product.generalDiscount === 'object') {
        // Если есть объект generalDiscount (из БД)
        generalDiscountData = {
          discountType: product.generalDiscount.discountType || 'none',
          discountValue: product.generalDiscount.discountValue || 0
        };
      } else if (product.generalDiscountType) {
        // Если есть отдельные поля (из API после обработки)
        generalDiscountData = {
          discountType: product.generalDiscountType || 'none',
          discountValue: product.generalDiscountValue || 0
        };
      } else if (product.discount > 0) {
        // Обратная совместимость со старым полем discount
        generalDiscountData = {
          discountType: 'percent',
          discountValue: product.discount
        };
      }
      
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price,
        stockQuantity: product.stockQuantity,
        unit: product.unit,
        multiplicity: product.multiplicity || 1,
        sellByBox: product.sellByBox || false,
        boxQuantity: product.boxQuantity || 1,
        boxPrice: product.boxPrice || '',
        category: product.category?._id || '',
        subCategory: product.subCategory || '',
        isActive: product.isActive,
        discount: product.discount || 0,
        generalDiscount: generalDiscountData,
        nutrition: product.nutrition || {
          calories: '',
          protein: '',
          fat: '',
          carbohydrates: '',
          fiber: '',
          servingSize: ''
        }
      });
    } else {
      setEditingProduct(null);
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      stockQuantity: '',
      unit: 'piece',
      multiplicity: 1,
      sellByBox: false,
      boxQuantity: 1,
      boxPrice: '',
      category: '',
      subCategory: '',
      isActive: true,
      discount: 0,
      generalDiscount: {
        discountType: 'none',
        discountValue: 0
      },
      nutrition: {
        calories: '',
        protein: '',
        fat: '',
        carbohydrates: '',
        fiber: '',
        servingSize: ''
      }
    });
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'nutrition' || key === 'generalDiscount') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      if (editingProduct) {
        await productsAPI.updateProduct(editingProduct._id, formDataToSend);
        setMessage({ type: 'success', text: 'Продукт успешно обновлен' });
      } else {
        await productsAPI.createProduct(formDataToSend);
        setMessage({ type: 'success', text: 'Продукт успешно создан' });
      }

      setShowModal(false);
      loadData();
      resetForm();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка при сохранении продукта' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот продукт?')) {
      return;
    }

    try {
      await productsAPI.deleteProduct(id);
      setMessage({ type: 'success', text: 'Продукт удален' });
      loadData();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка при удалении продукта' 
      });
    }
  };

  const handleStockInputChange = (productId, value) => {
    // Обновляем локальное значение немедленно для плавного ввода
    setLocalStockValues(prev => ({
      ...prev,
      [productId]: value
    }));

    // Очищаем предыдущий таймер для этого продукта
    if (stockUpdateTimers.current[productId]) {
      clearTimeout(stockUpdateTimers.current[productId]);
    }

    // Устанавливаем новый таймер для отправки на сервер (debounce)
    stockUpdateTimers.current[productId] = setTimeout(() => {
      handleStockUpdate(productId, value);
    }, 800); // Отправка через 800мс после последнего изменения
  };

  const handleStockUpdate = async (id, value) => {
    // Валидация значения
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) {
      // Если значение невалидное, возвращаем старое
      const originalProduct = products.find(p => p._id === id);
      if (originalProduct) {
        setLocalStockValues(prev => ({
          ...prev,
          [id]: originalProduct.stockQuantity
        }));
      }
      return;
    }

    try {
      // Отправляем на сервер
      await productsAPI.updateStock(id, { quantity: numValue, operation: 'set' });
      
      // Обновляем только этот продукт в списке, без полной перезагрузки
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product._id === id 
            ? { ...product, stockQuantity: numValue }
            : product
        )
      );
      
      // Синхронизируем локальное значение с успешно сохраненным
      setLocalStockValues(prev => {
        const newValues = { ...prev };
        delete newValues[id]; // Удаляем локальное значение, чтобы использовалось значение из products
        return newValues;
      });
    } catch (error) {
      console.error('Ошибка обновления количества:', error);
      alert('Ошибка обновления количества');
      
      // При ошибке возвращаем старое значение
      const originalProduct = products.find(p => p._id === id);
      if (originalProduct) {
        setLocalStockValues(prev => ({
          ...prev,
          [id]: originalProduct.stockQuantity
        }));
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setMessage({ type: 'error', text: 'Выберите файл для загрузки' });
      return;
    }

    setImportLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const json = JSON.parse(e.target.result);
          
          // API теперь поддерживает как массив, так и объект с ключами
          let response;
          if (importType === 'categories') {
            response = await categoriesAPI.importCategories(json);
          } else {
            response = await productsAPI.importProducts(json);
          }

          setMessage({ 
            type: 'success', 
            text: response.data.message 
          });
          setShowImportModal(false);
          setImportFile(null);
          loadData();
        } catch (parseError) {
          console.error('Parse error:', parseError);
          setMessage({ 
            type: 'error', 
            text: parseError.response?.data?.message || 'Ошибка чтения файла. Убедитесь, что это правильный JSON файл.' 
          });
        } finally {
          setImportLoading(false);
        }
      };
      reader.readAsText(importFile);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка при импорте' 
      });
      setImportLoading(false);
    }
  };

  const handleOpenImportModal = (type) => {
    setImportType(type);
    setImportFile(null);
    setShowImportModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getDiscountValue = (product) => {
    if (product.generalDiscountType && product.generalDiscountType !== 'none' && product.generalDiscountValue > 0) {
      return product.generalDiscountValue;
    }
    if (product.generalDiscount && product.generalDiscount.discountType !== 'none' && product.generalDiscount.discountValue > 0) {
      return product.generalDiscount.discountValue;
    }
    return 0;
  };

  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !filterCategory || product.category?._id === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'stock':
          aValue = a.stockQuantity;
          bValue = b.stockQuantity;
          break;
        case 'status':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        case 'discount':
          aValue = getDiscountValue(a);
          bValue = getDiscountValue(b);
          break;
        case 'popularity':
          // Используем данные популярности, если есть
          aValue = a.popularity ? (a.popularity.orderCount || 0) : 0;
          bValue = b.popularity ? (b.popularity.orderCount || 0) : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const selectedCategory = categories.find(c => c._id === formData.category);
  const subCategories = selectedCategory?.subCategories || [];

  return (
    <div className="admin-products-page">
      <div className="admin-products-header">
        <h1>Управление продуктами</h1>
        <div className="header-actions">
          <button onClick={() => handleOpenImportModal('categories')} className="btn-import">
            <FaFileImport /> Импорт категорий
          </button>
          <button onClick={() => handleOpenImportModal('products')} className="btn-import">
            <FaUpload /> Импорт продуктов
          </button>
          <button onClick={() => handleOpenModal()} className="btn-add">
            <FaPlus /> Добавить продукт
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="products-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Поиск продуктов..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ marginLeft: "8px" }}
          />
        </div>

        <select 
          value={filterCategory} 
          onChange={(e) => setFilterCategory(e.target.value)}
          className="filter-select"
        >
          <option value="">Все категории</option>
          {categories.map(cat => (
            <option key={cat._id} value={cat._id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <div className="products-table">
          <table>
            <thead>
              <tr>
                <th>Изображение</th>
                <th 
                  onClick={() => handleSort('name')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по названию"
                >
                  Название {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Категория</th>
                <th 
                  onClick={() => handleSort('price')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по цене"
                >
                  Цена {sortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('discount')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по скидке"
                >
                  {t(`discount`, 'Discount')} {sortField === 'discount' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('popularity')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по популярности"
                >
                  Популярность {sortField === 'popularity' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('stock')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по остаткам"
                >
                  Склад {sortField === 'stock' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Ед. изм.</th>
                <th 
                  onClick={() => handleSort('status')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Сортировать по статусу"
                >
                  Статус {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product._id}>
                  <td>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img 
                        src={product.effectiveImageUrl || product.imageUrl || '/images/placeholder.jpg'} 
                        alt={product.name}
                        className="product-thumb"
                        onError={(e) => e.target.src = '/images/placeholder.jpg'}
                      />
                      {product.imageSetByAdmin ? (
                        <span 
                          style={{
                            position: 'absolute',
                            bottom: '-4px',
                            right: '-4px',
                            background: '#2ecc71',
                            color: 'white',
                            fontSize: '9px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontWeight: 'bold',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} 
                          title="Картинка установлена админом для этого товара"
                        >
                          Своя
                        </span>
                      ) : (product.effectiveImageUrl && product.effectiveImageUrl !== '/images/placeholder.jpg' && product.effectiveImageUrl !== product.imageUrl) ? (
                        <span 
                          style={{
                            position: 'absolute',
                            bottom: '-4px',
                            right: '-4px',
                            background: '#3498db',
                            color: 'white',
                            fontSize: '9px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontWeight: 'bold',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} 
                          title={`Картинка унаследована от подгруппы: ${product.nestedGroupName || ''}`}
                        >
                          Группа
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>{product.name}</td>
                  <td>
                    {product.category?.name}
                    {product.subCategory && <><br/><small>{product.subCategory}</small></>}
                  </td>
                  <td>
                    {product.price.toFixed(2)} €
                    {(() => {
                      const discountType = product.generalDiscountType || product.generalDiscount?.discountType;
                      const discountValue = product.generalDiscountValue || product.generalDiscount?.discountValue || 0;
                      
                      if (discountType && discountType !== 'none' && discountValue > 0) {
                        let newPrice = product.price;
                        
                        if (discountType === 'percent') {
                          newPrice = product.price * (1 - discountValue / 100);
                        } else if (discountType === 'fixed') {
                          newPrice = Math.max(0, product.price - discountValue);
                        } else if (discountType === 'fixed_price') {
                          newPrice = discountValue;
                        }
                        
                        return (
                          <>
                            <br/>
                            <small style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                              {newPrice.toFixed(2)} €
                            </small>
                          </>
                        );
                      } else if (product.discount > 0) {
                        // Старая скидка для обратной совместимости
                        return (
                          <>
                            <br/>
                            <small style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                              {(product.price * (1 - product.discount / 100)).toFixed(2)} €
                            </small>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const discountType = product.generalDiscountType || product.generalDiscount?.discountType;
                      const discountValue = product.generalDiscountValue || product.generalDiscount?.discountValue || 0;
                      
                      if (discountType && discountType !== 'none' && discountValue > 0) {
                        if (discountType === 'percent') {
                          return (
                            <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                              -{discountValue}%
                            </span>
                          );
                        } else if (discountType === 'fixed') {
                          return (
                            <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                              -{discountValue.toFixed(2)}€
                            </span>
                          );
                        } else if (discountType === 'fixed_price') {
                          const discountPercent = ((product.price - discountValue) / product.price * 100).toFixed(0);
                          return (
                            <span style={{ color: '#e74c3c', fontWeight: 'bold' }} title={`Цена со скидкой: ${discountValue.toFixed(2)}€`}>
                              -{discountPercent}%
                            </span>
                          );
                        }
                      }
                      return <span style={{ color: '#999' }}>—</span>;
                    })()}
                  </td>
                  <td>
                    {product.popularity ? (
                      <span title={`Заказов: ${product.popularity.orderCount}, Куплено: ${product.popularity.totalQuantity}`}>
                        {product.popularity.orderCount > 0 ? (
                          <span style={{ color: '#27ae60', fontWeight: 'bold' }}>
                            {product.popularity.orderCount} ✓
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: '#999' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="stock-control">
                      <input
                        type="number"
                        value={localStockValues[product._id] ?? product.stockQuantity}
                        onChange={(e) => handleStockInputChange(product._id, e.target.value)}
                        className="stock-input"
                        min="0"
                      />
                    </div>
                  </td>
                  <td>{product.unit}</td>
                  <td>
                    <span className={`status-badge ${product.isActive ? 'active' : 'inactive'}`}>
                      {product.isActive ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleOpenModal(product)}
                        className="btn-icon edit"
                        title="Редактировать"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(product._id)}
                        className="btn-icon delete"
                        title="Удалить"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Редактировать продукт' : 'Новый продукт'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Категория *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value, subCategory: '' })}
                    required
                  >
                    <option value="">Выберите категорию</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {subCategories.length > 0 && (
                <div className="form-group">
                  <label>Подкатегория</label>
                  <select
                    value={formData.subCategory}
                    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                  >
                    <option value="">Выберите подкатегорию</option>
                    {subCategories.map(sub => (
                      <option key={sub._id} value={sub.code}>{sub.name} ({sub.code})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t(`price`, 'Price')} * (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Количество на складе *</label>
                  <input
                    type="number"
                    value={formData.stockQuantity}
                    onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Единица измерения *</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value, multiplicity: e.target.value === 'kg' ? formData.multiplicity : 1 })}
                  >
                    <option value="kg">Килограммы</option>
                    <option value="piece">Штуки</option>
                    {/* <option value="box">Ящики</option> */}
                  </select>
                </div>
              </div>

              {formData.unit === 'kg' && (
                <div className="form-group">
                  <label>Кратность (шаг добавления в корзину, кг)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={formData.multiplicity}
                    onChange={(e) => setFormData({ ...formData, multiplicity: parseFloat(e.target.value) || 1 })}
                    placeholder="Например: 9 - покупатель добавляет по 9 кг"
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '4px' }}>
                    При добавлении товара в корзину количество будет увеличиваться с шагом {formData.multiplicity || 1} кг
                  </small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Тип общей скидки</label>
                  <select
                    value={formData.generalDiscount.discountType}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      generalDiscount: { 
                        ...formData.generalDiscount, 
                        discountType: e.target.value,
                        discountValue: e.target.value === 'none' ? 0 : formData.generalDiscount.discountValue
                      } 
                    })}
                  >
                    <option value="none">Без скидки</option>
                    <option value="percent">Процентная скидка</option>
                    <option value="fixed">Фиксированная скидка</option>
                    <option value="fixed_price">Цена со скидкой</option>
                  </select>
                </div>

                {formData.generalDiscount.discountType !== 'none' && (
                  <div className="form-group">
                    <label>
                      {formData.generalDiscount.discountType === 'percent' 
                        ? 'Размер скидки (%)' 
                        : formData.generalDiscount.discountType === 'fixed_price'
                        ? 'Цена со скидкой (€)'
                        : 'Размер скидки (€)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={formData.generalDiscount.discountType === 'percent' ? "100" : undefined}
                      step={formData.generalDiscount.discountType === 'percent' ? "1" : "0.01"}
                      value={formData.generalDiscount.discountValue}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        generalDiscount: { 
                          ...formData.generalDiscount, 
                          discountValue: e.target.value 
                        } 
                      })}
                      placeholder={formData.generalDiscount.discountType === 'fixed_price' ? 'Новая цена' : '0'}
                    />
                    {formData.generalDiscount.discountType === 'fixed_price' && formData.price && formData.generalDiscount.discountValue && (
                      <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                        Скидка: {((formData.price - formData.generalDiscount.discountValue) / formData.price * 100).toFixed(0)}% 
                        ({(formData.price - formData.generalDiscount.discountValue).toFixed(2)}€)
                      </small>
                    )}
                  </div>
                )}
              </div>

              {formData.generalDiscount.discountType !== 'none' && (
                <div className="form-group">
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    Если у пользователя есть персональная скидка, будет применена большая из двух
                  </small>
                </div>
              )}

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.sellByBox}
                    onChange={(e) => setFormData({ ...formData, sellByBox: e.target.checked })}
                  />
                  Продажа ящиками
                </label>
              </div>

              {formData.sellByBox && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Количество в ящике</label>
                    <input
                      type="number"
                      value={formData.boxQuantity}
                      onChange={(e) => setFormData({ ...formData, boxQuantity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Цена ящика (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.boxPrice}
                      onChange={(e) => setFormData({ ...formData, boxPrice: e.target.value })}
                      placeholder="Рассчитается автоматически"
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>
                  <FaImage /> Изображение
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                />
                {editingProduct?.imageUrl && !imageFile && (
                  <small>Текущее изображение сохранится, если не загрузить новое</small>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Активен
                </label>
              </div>

              <details className="nutrition-details">
                <summary>Пищевая ценность (опционально)</summary>
                <div className="form-row">
                  <div className="form-group">
                    <label>Размер порции</label>
                    <input
                      type="text"
                      value={formData.nutrition.servingSize}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, servingSize: e.target.value }
                      })}
                      placeholder="100g"
                    />
                  </div>
                  <div className="form-group">
                    <label>Калории (ккал)</label>
                    <input
                      type="number"
                      value={formData.nutrition.calories}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, calories: e.target.value }
                      })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Белки (г)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.nutrition.protein}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, protein: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Жиры (г)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.nutrition.fat}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, fat: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Углеводы (г)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.nutrition.carbohydrates}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, carbohydrates: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Клетчатка (г)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.nutrition.fiber}
                      onChange={(e) => setFormData({
                        ...formData,
                        nutrition: { ...formData.nutrition, fiber: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </details>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Сохранить изменения' : 'Создать продукт'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {importType === 'categories' ? 'Импорт категорий' : 'Импорт продуктов'}
              </h2>
              <button onClick={() => setShowImportModal(false)} className="modal-close">×</button>
            </div>

            <div className="import-content">
              <div className="import-info">
                <p>
                  <strong>Формат файла:</strong> JSON
                </p>
                <p>
                  {importType === 'categories' 
                    ? 'Файл должен содержать массив категорий или объект с полем "categories"' 
                    : 'Файл должен содержать массив продуктов или объект с полем "products"'}
                </p>
                <p>
                  <strong>Примеры:</strong>
                </p>
                <p style={{ fontSize: '0.85rem', color: '#666' }}>
                  {importType === 'categories' 
                    ? '• [...] или { "categories": [...] }' 
                    : '• [...] или { "products": [...] }'}
                </p>
                <p className="import-note">
                  ⚠️ Если запись с таким же {importType === 'categories' ? 'кодом' : 'названием'} уже существует, она будет обновлена.
                </p>
              </div>

              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  id="import-file"
                  style={{ display: 'none' }}
                />
                <label htmlFor="import-file" className="file-upload-label">
                  <FaUpload size={40} />
                  <span>{importFile ? importFile.name : 'Выберите JSON файл'}</span>
                </label>
              </div>

              <div className="import-actions">
                <button 
                  onClick={handleImport} 
                  className="btn-primary"
                  disabled={!importFile || importLoading}
                >
                  {importLoading ? 'Загрузка...' : 'Импортировать'}
                </button>
                <button 
                  onClick={() => setShowImportModal(false)} 
                  className="btn-secondary"
                  disabled={importLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;

